export type SupplyStatusDto =
  | string
  | {
      code?: string;
      name?: string;
      label?: string;
      value?: string;
      tone?: string;
    };

export type SupplyRelationRef = {
  uuid: string;
  name: string;
};

export type SupplyClassificationValue = 'PRODUCT' | 'SERVICE';

export type SupplyClassificationRef = {
  uuid: string;
  name: string;
  code?: SupplyClassificationValue;
};

export type SupplyAccountingAccountDto = {
  uuid: string;
  name: string;
  accountNumber: string;
  formattedAccountNumber: string;
  source: SupplyRelationRef | null;
  type: SupplyRelationRef | null;
  affectable: boolean;
  selectable: boolean;
};

export type SupplyCompanyAccountDto = {
  company: SupplyRelationRef;
  accountingAccount: SupplyAccountingAccountDto | null;
};

export type SupplyCompanyAccountRequest = {
  companyUuid: string;
  accountingAccountUuid: string | null;
};

export type SupplyAccountingSelection = {
  companyUuid: string;
  initialAccountUuid: string | null;
  selectedAccountUuid: string | null;
};

export type SupplyDto = {
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  classification?: SupplyClassificationRef | SupplyClassificationValue | null;
  type?: SupplyRelationRef | null;
  group?: SupplyRelationRef | null;
  measurementUnit?: SupplyRelationRef | null;
  typeUuid?: string;
  groupUuid?: string;
  measurementUnitUuid?: string;
  companyAccounts?: SupplyCompanyAccountDto[];
  status: SupplyStatusDto;
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type SupplyRequest = {
  code: string;
  name: string;
  classificationUuid: string;
  typeUuid: string;
  groupUuid: string;
  measurementUnitUuid: string;
  description: string | null;
  companyAccounts?: SupplyCompanyAccountRequest[];
};

/** Omite las empresas sin cambio. Una selección vacía se conserva como `null`. */
export function supplyAccountingChanges(
  rows: readonly SupplyAccountingSelection[],
): SupplyCompanyAccountRequest[] {
  return rows.flatMap(row =>
    row.selectedAccountUuid === row.initialAccountUuid
      ? []
      : [
          {
            companyUuid: row.companyUuid,
            accountingAccountUuid: row.selectedAccountUuid,
          },
        ],
  );
}

export type SupplyListQuery = {
  code?: string;
  name?: string;
  description?: string;
  classification?: string;
  typeUuids?: string[];
  groupUuids?: string[];
  measurementUnitUuids?: string[];
  status?: string[];
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
};

export type SupplyListResult = {
  items: SupplyDto[];
  total: number;
};

export type SupplyFormSelects = {
  classifications: SupplyClassificationRef[];
  types: SupplyRelationRef[];
  groups: SupplyRelationRef[];
  measurementUnits: SupplyRelationRef[];
};

export type SupplyFilterOption = {
  name: string;
  value: string;
};

export type SupplyFilterSelects = {
  status: SupplyFilterOption[];
  types: SupplyFilterOption[];
  groups: SupplyFilterOption[];
};

export function supplyClassificationValue(
  dto: Pick<SupplyDto, 'classification'>,
): string | null {
  const classification = dto.classification;
  if (typeof classification === 'string' && classification.trim()) {
    return classification.trim();
  }
  if (classification && typeof classification === 'object' && classification.uuid) {
    return classification.uuid;
  }
  return null;
}

export type SupplyStatusLabel = 'Activo' | 'Inactivo';

export function supplyStatusLabel(dto: SupplyDto): SupplyStatusLabel {
  const status = dto.status;
  if (status && typeof status === 'object') {
    if (status.tone === 'danger') return 'Inactivo';
    if (status.tone === 'success') return 'Activo';
    return classifyStatusName(status.name ?? status.label ?? status.code ?? '');
  }
  if (typeof status === 'string') {
    return classifyStatusName(status);
  }
  if (typeof dto.enabled === 'boolean') {
    return dto.enabled ? 'Activo' : 'Inactivo';
  }
  return 'Activo';
}

function classifyStatusName(raw: string): SupplyStatusLabel {
  const normalized = raw.trim().toLowerCase();
  if (
    normalized === 'inactivo' ||
    normalized === 'inactive' ||
    normalized === 'disabled' ||
    normalized.startsWith('inactiv')
  ) {
    return 'Inactivo';
  }
  return 'Activo';
}
